import React from "react";
import { FiX, FiBarChart2, FiCalendar, FiExternalLink } from "react-icons/fi";

export default function SubmissionHistoryModal({ assignment, histories = [], onClose, onOpenResult }) {
  // Helper: lấy epoch seconds từ các dạng timestamp (seconds, _seconds, number ms, Date string)
  const tsSeconds = (ts) => {
    if (!ts) return 0;
    if (typeof ts === "number") return ts > 1e12 ? Math.floor(ts / 1000) : ts; // ms hoặc s
    if (typeof ts === "string") return Math.floor(new Date(ts).getTime() / 1000);
    if (typeof ts === "object") {
      if (typeof ts.seconds === "number") return ts.seconds;
      if (typeof ts._seconds === "number") return ts._seconds;
      if (typeof ts.toDate === "function") return Math.floor(ts.toDate().getTime() / 1000);
    }
    return 0;
  };

  const getBestTimestamp = (h) =>
    tsSeconds(h.submittedAt) ||
    tsSeconds(h.startedAt) ||
    tsSeconds(h.createdAt) ||
    tsSeconds(h.updatedAt);

  const sorted = [...histories].sort((a, b) => getBestTimestamp(b) - getBestTimestamp(a));
  const fmtTime = (ts) => {
    const s = tsSeconds(ts);
    if (!s) return "—";
    return new Date(s * 1000).toLocaleString();
  };

  const fmtDuration = (sec) => {
    if (!Number.isFinite(sec) || sec <= 0) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
  };

  // Chuẩn hoá score/percent từ schema mới
  const getScoreInfo = (h) => {
    const total = typeof h?.score?.total === "number" ? h.score.total : 0;
    const correct = typeof h?.score?.correct === "number" ? h.score.correct : 0;
    let percent = typeof h?.percent === "number" && Number.isFinite(h.percent)
      ? Math.round(h.percent)
      : (total > 0 ? Math.round((correct / total) * 100) : 0);
    return { correct, total, percent };
  };

  const statusLabel = (st) => {
    switch ((st || "").toLowerCase()) {
      case "finished":
      case "submitted":
        return "Đã nộp";
      case "in_progress":
      case "draft":
        return "Nháp";
      case "incomplete":
        return "Chưa hoàn thành";
      default:
        return st || "—";
    }
  };

  return (
    <div className="role-theme-surface fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-2xl rounded-2xl bg-white text-slate-900 shadow-2xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Lịch sử nộp — {assignment?.title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <FiX />
          </button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-auto">
          {sorted.length === 0 ? (
            <p className="opacity-70">Chưa có lần nộp nào.</p>
          ) : (
            <ul className="space-y-3">
              {sorted.map((h, i) => {
                const { correct, total, percent } = getScoreInfo(h);
                // Không có attemptNo trong schema -> đánh số theo thứ tự mới nhất
                const attemptOrder = h.attemptNo ?? (sorted.length - i);
                const canOpenResult = h.reviewable !== false;

                return (
                  <li key={h.id || `${tsSeconds(h.startedAt)}-${tsSeconds(h.submittedAt)}`}>
                    <div className="rounded-xl border p-3 flex items-center justify-between gap-3">
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          Lần {attemptOrder} — {statusLabel(h.status)}
                        </div>

                        <div className="text-xs flex flex-wrap items-center gap-x-4 gap-y-1 opacity-80">
                          <span className="inline-flex items-center gap-1">
                            <FiCalendar /> Bắt đầu: {fmtTime(h.startedAt)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <FiCalendar /> Nộp: {fmtTime(h.submittedAt)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            ⏱ Thời lượng: {fmtDuration(h.duration)}
                          </span>
                          {h.category && <span className="inline-flex items-center gap-1">🏷 {h.category}</span>}
                          {h.level && <span className="inline-flex items-center gap-1">🎯 {h.level}</span>}
                          {Number.isFinite(h.parts) && <span className="inline-flex items-center gap-1">🧩 {h.parts} parts</span>}
                        </div>

                        <div className="text-sm inline-flex items-center gap-1">
                          <FiBarChart2 />
                          Điểm: {correct}/{total} ({percent}%)
                        </div>
                      </div>

                      <button
                        onClick={() => canOpenResult && onOpenResult && onOpenResult(h.testId, h.id)}
                        disabled={!canOpenResult}
                        className={`px-3 py-2 rounded-xl border inline-flex items-center gap-2 ${canOpenResult ? "hover:bg-slate-50" : "opacity-60 cursor-not-allowed"}`}
                        title={canOpenResult ? "" : "Bài này không cho xem lại kết quả"}
                      >
                        <FiExternalLink />
                        <span>Xem kết quả</span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-3 border-t text-right">
          <button onClick={onClose} className="px-3 py-2 rounded-xl border hover:bg-slate-50">Đóng</button>
        </div>
      </div>
    </div>
  );
}
